/**
 * PermissionsContext — CRM
 *
 * Fetches module-level permissions from the HRMS backend
 * (GET /api/permissions/my-role?app=crm) using the hrms_token stored
 * in localStorage. This allows Access Control settings configured in
 * HRMS to apply to the CRM sidebar without re-implementing the
 * permissions table in the CRM backend.
 *
 * Usage:
 *   const { can } = usePermissions();
 *   can('pipeline')  → true/false
 *   can('analytics') → true/false
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const PermissionsContext = createContext(null);

// Base HRMS API URL — CRM fetches permissions cross-origin from HRMS
const HRMS_API = process.env.REACT_APP_HRMS_API_URL || "http://localhost:5000/api";

// Roles that use individual grants only — no role-based fallback.
// Superadmin, admin, and executive keep role-based permissions.
const INDIVIDUAL_ONLY_ROLES = new Set(["employee", "team_leader", "manager"]);

// ── Fallback defaults (used when HRMS API is unreachable) ─────────────────
// employee / team_leader / manager: intentionally empty — access comes only
// from individual module grants (Access Control → Individual Access → Grant).
const FALLBACK_DEFAULTS = {
  employee:    {}, // Individual grants only — no role-based defaults
  team_leader: {}, // Individual grants only — no role-based defaults
  manager:     {}, // Individual grants only — no role-based defaults
  admin:       null,  // null = grant all
  superadmin:  null,
  executive:   { audit_log:1 },
};

const DEFAULT_SCOPE = {
  employee: "self", team_leader: "team", manager: "team",
  admin: "company", superadmin: "all", executive: "all",
};

export function PermissionsProvider({ user, children }) {
  const [perms, setPerms]           = useState(null);
  const [empOverrides, setEmpOverrides] = useState({});
  const [loaded, setLoaded]         = useState(false);

  const loadPerms = useCallback(async () => {
    if (!user) { setPerms(null); setEmpOverrides({}); setLoaded(true); return; }

    // Use the HRMS token for the HRMS permissions endpoint
    const hrmsToken = localStorage.getItem("hrms_token");
    if (!hrmsToken) { setPerms(null); setEmpOverrides({}); setLoaded(true); return; }

    try {
      const res = await fetch(`${HRMS_API}/permissions/my-role?app=crm`, {
        headers: { Authorization: `Bearer ${hrmsToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const rows = data.permissions || [];
      const map  = {};
      for (const r of rows) {
        map[r.module_key] = {
          can_view:   !!r.can_view,
          can_create: !!r.can_create,
          can_edit:   !!r.can_edit,
          can_delete: !!r.can_delete,
          data_scope: r.data_scope || DEFAULT_SCOPE[user.role] || "self",
          is_enabled: r.is_enabled !== false,
        };
      }
      setPerms(map);
      setEmpOverrides(data.employee_overrides || {});
    } catch {
      // Silently fall back — app still works with defaults
      setPerms(null);
      setEmpOverrides({});
    } finally {
      setLoaded(true);
    }
  }, [user?.id, user?.role]);

  useEffect(() => { loadPerms(); }, [loadPerms]);

  // ── can(moduleKey, action) ────────────────────────────────────────────────
  // Resolution order:
  //   1. Individual CRM override (grant → true, deny → false)
  //   2. employee / team_leader / manager → hard-stop false (individual grants only)
  //   3. superadmin / admin → full access
  //   4. executive / others → role-based DB permissions / fallback
  const can = useCallback((moduleKey, action = "view") => {
    if (!user) return false;

    // ── Step 1: individual CRM override (highest priority for all roles) ──
    const crmOv = empOverrides?.crm || {};
    if (crmOv[moduleKey] === "deny")  return false;
    if (crmOv[moduleKey] === "grant") return true;

    // ── Step 2: employee / team_leader / manager — individual grants only ──
    // No 'grant' override above → module is hidden. No role-based fallback.
    if (INDIVIDUAL_ONLY_ROLES.has(user.role)) return false;

    // ── Step 3: superadmin + admin — full access ──
    if (user.role === "superadmin" || user.role === "admin") {
      if (!perms) return true;
      const m = perms[moduleKey];
      if (!m) return true;
      if (!m.is_enabled) return false;
      return m[`can_${action}`] !== false;
    }

    // ── Step 4: executive / others — role-based permissions ──
    if (!perms) {
      const fallback = FALLBACK_DEFAULTS[user.role];
      if (fallback === null) return true;
      if (!fallback)         return false;
      return !!fallback[moduleKey];
    }

    const m = perms[moduleKey];
    if (!m) return false;
    if (!m.is_enabled) return false;
    return !!m[`can_${action}`];
  }, [user, perms, empOverrides]);

  const reloadPermissions = useCallback(() => {
    setLoaded(false);
    loadPerms();
  }, [loadPerms]);

  return (
    <PermissionsContext.Provider value={{ can, perms, empOverrides, loaded, reloadPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  // Safe defaults if used outside provider
  if (!ctx) return {
    can: () => true,
    perms: null,
    loaded: true,
    reloadPermissions: () => {},
  };
  return ctx;
}
